const resourceProductId = (resource) =>
  Number(resource?.product_id ?? resource?.product?.id ?? 0);

const resourceQuantity = (resource) =>
  Math.max(1, Number.parseInt(resource?.quantity, 10) || 1);

const requirementCount = (resource, requirementId) => {
  const requirement = (resource?.product?.personnel_requirements ?? []).find(
    (candidate) => Number(candidate.id) === Number(requirementId),
  );

  return Math.max(1, Number(requirement?.required_count) || 1);
};

const distributeAssignments = (resources) => {
  const assignments = resources.flatMap(
    (resource) => resource?.personnel_assignments ?? [],
  );

  resources.forEach((resource) => {
    resource.personnel_assignments = [];
  });

  const assignmentsByRequirement = assignments.reduce((groups, assignment) => {
    const key = Number(assignment?.product_personnel_requirement_id ?? 0);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(assignment);
    return groups;
  }, new Map());

  assignmentsByRequirement.forEach((requirementAssignments, requirementId) => {
    let assignmentIndex = 0;

    resources.forEach((resource) => {
      const capacity =
        resourceQuantity(resource) * requirementCount(resource, requirementId);

      for (
        let slot = 0;
        slot < capacity && assignmentIndex < requirementAssignments.length;
        slot += 1
      ) {
        resource.personnel_assignments.push(
          requirementAssignments[assignmentIndex],
        );
        assignmentIndex += 1;
      }
    });

    // Never hide or discard historic over-allocations.
    if (assignmentIndex < requirementAssignments.length) {
      resources[resources.length - 1].personnel_assignments.push(
        ...requirementAssignments.slice(assignmentIndex),
      );
    }
  });
};

/**
 * Older resource payloads can contain both N duplicate rows and quantity=N on
 * every row. Treat that as N units in total, not N x N units, while retaining
 * a single quantity-bearing row when that is how the resource was stored.
 */
export const normalizeResourceAllocationGroups = (resources = []) => {
  if (!Array.isArray(resources) || resources.length === 0) return resources;

  const groups = resources.reduce((map, resource) => {
    const key = resourceProductId(resource);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(resource);
    return map;
  }, new Map());

  groups.forEach((group) => {
    if (group.length < 2) return;

    const totalUnits = Math.max(
      group.length,
      ...group.map(resourceQuantity),
    );
    const unitsPerRow = Math.floor(totalUnits / group.length);
    const remainder = totalUnits % group.length;

    group.forEach((resource, index) => {
      resource.quantity = unitsPerRow + (index < remainder ? 1 : 0);
    });

    distributeAssignments(group);
  });

  return resources;
};
